import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { createElement } from 'react';

import '@/design-system/index.css';
import { env } from '@/lib/env';
import { setTelemetry, telemetry } from '@/lib/telemetry';
import { createRumTelemetry } from '@/lib/telemetry/rum';
import { router } from './router';

/*
 * Boot sequence (spec §4.1):
 *   validate env (import side-effect) → init telemetry → conditional MSW →
 *   mount RouterProvider inside StrictMode.
 */
export async function bootstrap(): Promise<void> {
  // env is validated on import; referencing it keeps the dependency explicit.
  void env.API_BASE_URL;

  const rum = createRumTelemetry();
  if (rum) {
    setTelemetry(rum);
    telemetry.init();
  }

  if (env.ENABLE_MOCK) {
    const { worker } = await import('../../test/msw/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  } else if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    // Mocks are OFF. A service worker registered by MSW during an earlier
    // VITE_ENABLE_MOCK=true run outlives reloads and server restarts and can
    // leave the tab under a stale controller (symptom: the page renders but is
    // inert). Proactively unregister any leftover worker so a mocks-off run
    // always starts from a clean, uncontrolled tab. Best-effort; never blocks
    // the mount.
    void navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
  }

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root not found');

  createRoot(rootEl).render(
    createElement(StrictMode, null, createElement(RouterProvider, { router })),
  );
}
